import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';

import { apiFactory, UserDataClient, UserDataKey, type UserData as UserDataType } from '@app/client';
import { SidebarGroup, type SidebarGroupInterface, SidebarYat } from '@app/helpers';

import { type RootState } from '../reducers';
import {
    ACTION_SET_EMAIL_PREFERENCES_IS_OPEN,
    ACTION_SET_MOBILE_SHARE_MODAL_IS_OPEN,
    ACTION_SET_MOBILE_SIDEBAR_IS_OPEN,
    ACTION_SET_MOBILE_TRANSFER_MODAL_IS_OPEN,
    ACTION_SET_MOBILE_USER_NAVIGATION_IS_OPEN,
    ACTION_SET_NEW_VERSION_AVAILABLE,
    ACTION_SET_SIDEBAR_GROUPS,
    ACTION_SET_SIDEBAR_IS_EXPANDED,
    ACTION_SET_TWITTER_CONNECT_IS_OPEN,
    ACTION_SET_YAT_REDIRECT_MODAL_IS_OPEN,
    type DashboardActionTypes,
} from '../types/dashboard';

export function setSidebarGroups(sidebarGroups: SidebarGroup): DashboardActionTypes {
    return {
        type: ACTION_SET_SIDEBAR_GROUPS,
        sidebarGroups,
    };
}

export function setNewVersionAvailable(refresh: boolean): DashboardActionTypes {
    return {
        type: ACTION_SET_NEW_VERSION_AVAILABLE,
        refresh,
    };
}

export function getDashboard(data?: UserDataType) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        let sidebarData;
        try {
            if (data && data.value) {
                sidebarData = SidebarGroup.fromObject(data.value as SidebarGroupInterface);
            } else {
                const storedData = await apiFactory().call(UserDataClient.getUserData, { key: UserDataKey.Sidebar });
                if (storedData.ok && storedData.value && storedData.value.value) {
                    sidebarData = SidebarGroup.fromObject(storedData.value.value as SidebarGroupInterface);
                } else {
                    sidebarData = new SidebarGroup({ children: [] });
                }
            }
        } catch (_) {
            // Set a default
            sidebarData = new SidebarGroup({ children: [] });
        }

        const displayYatRhythmScores = {};
        getState().userEmojis.extendedEmojis.forEach(({ display_format, rhythm_score = 0 }) => {
            displayYatRhythmScores[display_format] = rhythm_score || 0;
        });

        // Add any missing Yats to the sidebarData
        Object.keys(displayYatRhythmScores).forEach((displayYat) => {
            const rhythmScore = displayYatRhythmScores[displayYat];
            if (!sidebarData.includes(displayYat)) {
                sidebarData.children.push(new SidebarYat(displayYat, rhythmScore));
            }
        });

        // Remove any Yats that are no longer in the account
        sidebarData.pruneMissing(Object.keys(displayYatRhythmScores));

        dispatch(setSidebarGroups(sidebarData));
    };
}

export function storeDashboard(sidebar: SidebarGroup) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        // Store the data in redux
        dispatch(setSidebarGroups(sidebar));
        // Store the sidebar serialized data on the server
        await apiFactory().call(UserDataClient.putUserData, {
            key: UserDataKey.Sidebar,
            value: { value: sidebar.serialize() },
        });
    };
}

export const setIsExpanded = (sidebarIsExpanded: boolean) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: ACTION_SET_SIDEBAR_IS_EXPANDED,
        sidebarIsExpanded,
    });
};

export const setMobileSidebarIsOpen =
    (mobileSidebarIsOpen: boolean) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
        dispatch({
            type: ACTION_SET_MOBILE_SIDEBAR_IS_OPEN,
            mobileSidebarIsOpen,
        });
    };

export const setMobileUserNavigationIsOpen =
    (mobileUserNavigationIsOpen: boolean) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
        dispatch({
            type: ACTION_SET_MOBILE_USER_NAVIGATION_IS_OPEN,
            mobileUserNavigationIsOpen,
        });
    };

export const setMobileShareModalIsOpen =
    (mobileShareModalIsOpen: boolean) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
        dispatch({
            type: ACTION_SET_MOBILE_SHARE_MODAL_IS_OPEN,
            mobileShareModalIsOpen,
        });
    };

export const setMobileTransferModalIsOpen =
    (mobileTransferModalIsOpen: boolean) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
        dispatch({
            type: ACTION_SET_MOBILE_TRANSFER_MODAL_IS_OPEN,
            mobileTransferModalIsOpen,
        });
    };

export function setYatRedirectModalIsOpen(yatRedirectModalIsOpen: boolean): DashboardActionTypes {
    return {
        type: ACTION_SET_YAT_REDIRECT_MODAL_IS_OPEN,
        yatRedirectModalIsOpen,
    };
}

export function setTwitterConnectIsOpen(twitterConnectIsOpen: boolean): DashboardActionTypes {
    return {
        type: ACTION_SET_TWITTER_CONNECT_IS_OPEN,
        twitterConnectIsOpen,
    };
}
export function setEmailPreferencesIsOpen(emailPreferencesIsOpen: boolean): DashboardActionTypes {
    return {
        type: ACTION_SET_EMAIL_PREFERENCES_IS_OPEN,
        emailPreferencesIsOpen,
    };
}
